import { Button, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { AuditLog } from '@/types'
import { format } from 'date-fns'
import { parseISO } from 'date-fns'
import { QueryErrorState } from '@/components/common/QueryErrorState'

export interface AuditLogDetailModalProps {
  log: AuditLog | null
  isLoading: boolean
  error: Error | null
  open: boolean
  onClose: () => void
}

function formatTimestamp(iso: string): string {
  try {
    return format(parseISO(iso), 'PPpp')
  } catch {
    return iso
  }
}

function ChangesBlock({
  title,
  data,
}: {
  title: string
  data: Record<string, unknown> | undefined
}): React.ReactElement | null {
  if (!data || Object.keys(data).length === 0) return null
  return (
    <div>
      <div className="mb-1 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
        {title}
      </div>
      <pre className="max-h-40 overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  )
}

export function AuditLogDetailModal({
  log,
  isLoading,
  error,
  open,
  onClose,
}: AuditLogDetailModalProps): React.ReactElement {
  const showCloseFooter = Boolean(!isLoading && !error && log)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Audit log detail"
      className="max-w-2xl"
      footer={
        showCloseFooter ? (
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : null
      }
    >
      {isLoading && (
        <SectionLoader variant="detail" label="Loading audit entry" />
      )}
      {error && (
        <QueryErrorState error={error} size="inline" title="Couldn't load this audit entry" />
      )}
      {!isLoading && !error && log && (
        <div className="space-y-5 text-sm">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <dt className="text-gray-500 dark:text-gray-400">Time</dt>
            <dd className="font-medium">{formatTimestamp(log.timestamp)}</dd>
            <dt className="text-gray-500 dark:text-gray-400">Action</dt>
            <dd className="capitalize">{log.action}</dd>
            <dt className="text-gray-500 dark:text-gray-400">Module</dt>
            <dd>{log.module}</dd>
            <dt className="text-gray-500 dark:text-gray-400">Financial</dt>
            <dd>{log.isFinancial ? 'Yes' : 'No'}</dd>
          </dl>
          {log.actor && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                Actor
              </div>
              <p className="font-medium">
                {log.actor.name ?? log.actor.email ?? '—'}
              </p>
              {log.actor.email && (
                <p className="text-gray-600 dark:text-gray-400">
                  {log.actor.email}
                </p>
              )}
              {log.actor.role && (
                <p className="text-gray-500 dark:text-gray-500">
                  {log.actor.role}
                </p>
              )}
            </div>
          )}
          <div>
            <div className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
              Entity
            </div>
            <p>
              <span className="font-medium">{log.entity.type}</span>
              <span className="text-gray-600 dark:text-gray-400">
                {' '}
                (ID: {log.entity.id})
              </span>
            </p>
            {log.entity.description && (
              <p className="text-gray-600 dark:text-gray-400">
                {log.entity.description}
              </p>
            )}
          </div>
          {log.description && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                Description
              </div>
              <p>{log.description}</p>
            </div>
          )}
          {log.note && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                Note
              </div>
              <p>{log.note}</p>
            </div>
          )}
          {log.changes && (
            <div className="space-y-3">
              {log.changes.changedFields && log.changes.changedFields.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Changed fields: {log.changes.changedFields.join(', ')}
                </p>
              )}
              <ChangesBlock title="Before" data={log.changes.before} />
              <ChangesBlock title="After" data={log.changes.after} />
            </div>
          )}
          {log.request?.ipAddress && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              IP: {log.request.ipAddress}
              {log.request.source && ` · Source: ${log.request.source}`}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}

AuditLogDetailModal.displayName = 'AuditLogDetailModal'
